import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import {
  useGetMyBranchQuery,
  useUpdateBranchWorkingHoursMutation,
  useUpdateBranchBreaksMutation,
  useGetBranchAttendanceReportQuery,
} from '../../services/branchApi';

const BranchSettings = () => {
  const [workingHoursDialog, setWorkingHoursDialog] = useState(false);
  const [breaksDialog, setBreaksDialog] = useState(false);
  const [workingHoursData, setWorkingHoursData] = useState({
    startTime: '',
    endTime: '',
    gracePeriod: 15,
    overtimeThreshold: 8,
    earlyLeaveThreshold: 30,
  });
  const [breaksData, setBreaksData] = useState([]);
  const [newBreak, setNewBreak] = useState({
    name: '',
    startTime: '',
    endTime: '',
    duration: 0,
  });

  const { data: branchData, isLoading, error } = useGetMyBranchQuery();
  const [updateWorkingHours, { isLoading: updatingHours }] = useUpdateBranchWorkingHoursMutation();
  const [updateBreaks, { isLoading: updatingBreaks }] = useUpdateBranchBreaksMutation();

  const branch = branchData?.data;

  const { data: attendanceReport, isLoading: loadingReport } = useGetBranchAttendanceReportQuery(
    branch?._id ? { id: branch._id } : null,
    { skip: !branch?._id }
  );

  useEffect(() => {
    if (branch?.workingHours) {
      setWorkingHoursData({
        startTime: branch.workingHours.startTime || '',
        endTime: branch.workingHours.endTime || '',
        gracePeriod: branch.workingHours.gracePeriod || 15,
        overtimeThreshold: branch.workingHours.overtimeThreshold || 8,
        earlyLeaveThreshold: branch.workingHours.earlyLeaveThreshold || 30,
      });
    }
    if (branch?.breaks) {
      setBreaksData(branch.breaks);
    }
  }, [branch]);

  const handleWorkingHoursSubmit = async () => {
    if (!branch?._id) {
      console.error('No branch ID available');
      return;
    }
    
    try {
      await updateWorkingHours({
        id: branch._id,
        workingHours: workingHoursData,
      }).unwrap();
      setWorkingHoursDialog(false);
    } catch (error) {
      console.error('Failed to update working hours:', error);
    }
  };

  const handleAddBreak = () => {
    if (newBreak.name && newBreak.startTime && newBreak.endTime) {
      const start = new Date(`2000-01-01T${newBreak.startTime}`);
      const end = new Date(`2000-01-01T${newBreak.endTime}`);
      const duration = (end - start) / (1000 * 60); // duration in minutes

      const breakToAdd = {
        ...newBreak,
        duration,
      };

      setBreaksData([...breaksData, breakToAdd]);
      setNewBreak({ name: '', startTime: '', endTime: '', duration: 0 });
    }
  };

  const handleRemoveBreak = (index) => {
    setBreaksData(breaksData.filter((_, i) => i !== index));
  };

  const handleBreaksSubmit = async () => {
    if (!branch?._id) {
      console.error('No branch ID available');
      return;
    }
    
    try {
      await updateBreaks({
        id: branch._id,
        breaks: breaksData,
      }).unwrap();
      setBreaksDialog(false);
    } catch (error) {
      console.error('Failed to update breaks:', error);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BusinessIcon sx={{ mr: 2 }} />
          Branch Settings
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading branch settings...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Branch Settings Not Available
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error.status === 404 
              ? "You are not assigned to any branch. Please contact your administrator to assign you to a branch."
              : "Failed to load branch settings. Please try again."
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Error details: {error.data?.message || error.message || 'Unknown error'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Branch Settings - IAR HRMS</title>
      </Helmet>

      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BusinessIcon sx={{ mr: 2 }} />
          Branch Settings
        </Typography>

        {!branch && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              No Branch Information Available
            </Typography>
            <Typography variant="body2">
              {branchData?.message || "You may not be assigned to a specific branch, or branch data is not configured yet."}
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Working Hours Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ mr: 1 }} />
                    Working Hours
                  </Typography>
                  <IconButton onClick={() => setWorkingHoursDialog(true)} color="primary">
                    <EditIcon />
                  </IconButton>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography>
                    <strong>Start Time:</strong> {workingHoursData.startTime || 'Not set'}
                  </Typography>
                  <Typography>
                    <strong>End Time:</strong> {workingHoursData.endTime || 'Not set'}
                  </Typography>
                  <Typography>
                    <strong>Grace Period:</strong> {workingHoursData.gracePeriod} minutes
                  </Typography>
                  <Typography>
                    <strong>Overtime Threshold:</strong> {workingHoursData.overtimeThreshold} hours
                  </Typography>
                  <Typography>
                    <strong>Early Leave Threshold:</strong> {workingHoursData.earlyLeaveThreshold} minutes
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Breaks Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ mr: 1 }} />
                    Breaks
                  </Typography>
                  <IconButton onClick={() => setBreaksDialog(true)} color="primary">
                    <EditIcon />
                  </IconButton>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {breaksData.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {breaksData.map((breakItem, index) => (
                      <Chip
                        key={index}
                        label={`${breakItem.name} (${breakItem.startTime}-${breakItem.endTime})`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No breaks configured</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Attendance Report Card */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AssessmentIcon sx={{ mr: 1 }} />
                  Attendance Summary (Last 30 Days)
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {loadingReport ? (
                  <CircularProgress size={24} />
                ) : attendanceReport ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="h4" color="primary">
                        {attendanceReport.totalEmployees || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Employees
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="h4" color="success.main">
                        {attendanceReport.onTime || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        On Time
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="h4" color="warning.main">
                        {attendanceReport.late || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Late Arrivals
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="h4" color="error.main">
                        {attendanceReport.early || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Early Departures
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography color="text.secondary">No attendance data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Working Hours Dialog */}
        <Dialog open={workingHoursDialog} onClose={() => setWorkingHoursDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Configure Working Hours</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={workingHoursData.startTime}
                  onChange={(e) => setWorkingHoursData({ ...workingHoursData, startTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={workingHoursData.endTime}
                  onChange={(e) => setWorkingHoursData({ ...workingHoursData, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Grace Period (minutes)"
                  type="number"
                  value={workingHoursData.gracePeriod}
                  onChange={(e) => setWorkingHoursData({ ...workingHoursData, gracePeriod: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Overtime Threshold (hours)"
                  type="number"
                  value={workingHoursData.overtimeThreshold}
                  onChange={(e) => setWorkingHoursData({ ...workingHoursData, overtimeThreshold: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Early Leave Threshold (minutes)"
                  type="number"
                  value={workingHoursData.earlyLeaveThreshold}
                  onChange={(e) => setWorkingHoursData({ ...workingHoursData, earlyLeaveThreshold: parseInt(e.target.value) })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWorkingHoursDialog(false)}>Cancel</Button>
            <Button onClick={handleWorkingHoursSubmit} variant="contained" disabled={updatingHours}>
              {updatingHours ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Breaks Dialog */}
        <Dialog open={breaksDialog} onClose={() => setBreaksDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Configure Breaks</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Add New Break
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Break Name"
                  value={newBreak.name}
                  onChange={(e) => setNewBreak({ ...newBreak, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={newBreak.startTime}
                  onChange={(e) => setNewBreak({ ...newBreak, startTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={newBreak.endTime}
                  onChange={(e) => setNewBreak({ ...newBreak, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddBreak}
                  sx={{ height: '56px' }}
                >
                  Add
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Current Breaks
            </Typography>
            {breaksData.length > 0 ? (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breaksData.map((breakItem, index) => (
                      <TableRow key={index}>
                        <TableCell>{breakItem.name}</TableCell>
                        <TableCell>{breakItem.startTime}</TableCell>
                        <TableCell>{breakItem.endTime}</TableCell>
                        <TableCell>{breakItem.duration} min</TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleRemoveBreak(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No breaks configured
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBreaksDialog(false)}>Cancel</Button>
            <Button onClick={handleBreaksSubmit} variant="contained" disabled={updatingBreaks}>
              {updatingBreaks ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default BranchSettings;
